import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Pencil, Trash2, Star, X, ExternalLink } from "lucide-react";
import { api, waLink } from "../../lib/constants";
import { useAuth } from "../../context/AuthContext";
import { useSite } from "../../context/SiteContext";

const inp = "w-full bg-[#0e0e0e] border border-white/15 rounded-sm px-3 py-2.5 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";
const label = "overline-label text-[10px] text-zinc-400 block mb-1.5";

const TABS = [
  { key: "leads", label: "Leads" },
  { key: "plans", label: "Planos" },
  { key: "services", label: "Serviços" },
  { key: "results", label: "Resultados" },
  { key: "testimonials", label: "Depoimentos" },
  { key: "units", label: "Unidades" },
  { key: "faqs", label: "FAQ" },
  { key: "posts", label: "Blog" },
  { key: "site", label: "Textos & Contato" },
];

const SKIP_KEYS = ["id", "created_at", "key", "professionals", "images"];
const TEXTAREA_KEYS = ["description", "answer", "body", "text", "tagline", "excerpt"];

function FieldEditor({ fieldKey, value, onChange }) {
  if (Array.isArray(value)) {
    return (
      <div>
        <label className={label}>{fieldKey} (um por linha)</label>
        <textarea className={inp} rows={Math.max(3, value.length)} value={value.join("\n")}
          onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
      </div>
    );
  }
  if (typeof value === "boolean") {
    return (
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="accent-gold w-4 h-4" />
        <span className="text-sm text-zinc-300">{fieldKey}</span>
      </label>
    );
  }
  if (typeof value === "number") {
    return (<div><label className={label}>{fieldKey}</label><input type="number" className={inp} value={value} onChange={(e) => onChange(Number(e.target.value))} /></div>);
  }
  const long = TEXTAREA_KEYS.includes(fieldKey) || (value && value.length > 70);
  return (
    <div>
      <label className={label}>{fieldKey}</label>
      {long ? (
        <textarea className={inp} rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className={inp} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function ItemModal({ item, route, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.put(`/${route}/${form.id}`, form);
      else await api.post(`/${route}`, form);
      toast.success("Salvo com sucesso");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-[#0d0d0d] border border-white/15 rounded-sm w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0d0d0d]">
          <h3 className="font-heading font-600 uppercase text-white">{form.id ? "Editar" : "Novo"} item</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {Object.keys(form).filter((k) => !["id", "created_at"].includes(k)).map((k) => (
            <FieldEditor key={k} fieldKey={k} value={form[k]} onChange={(v) => set(k, v)} />
          ))}
        </div>
        <div className="p-5 border-t border-white/10 flex gap-3 justify-end sticky bottom-0 bg-[#0d0d0d]">
          <button onClick={onClose} className="px-5 py-2.5 border border-white/20 rounded-sm text-zinc-300 text-sm hover:border-white/40">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 bg-gold hover:bg-gold-hover text-black font-heading font-600 rounded-sm text-sm inline-flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function EntityManager({ route }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get(`/${route}`).then(({ data }) => setItems(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [route]);

  const addNew = () => {
    const base = items[0] ? JSON.parse(JSON.stringify(items[0])) : { name: "", order: 1 };
    delete base.id; delete base.created_at;
    base.order = (items.reduce((m, i) => Math.max(m, i.order || 0), 0)) + 1;
    setEditing(base);
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir este item?")) return;
    await api.delete(`/${route}/${id}`); toast.success("Excluído"); load();
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 text-gold animate-spin mx-auto" /></div>;

  return (
    <div>
      <div className="flex justify-end mb-5">
        <button onClick={addNew} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-black font-heading font-600 text-sm px-4 py-2.5 rounded-sm" data-testid="admin-add-item">
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((it) => (
          <div key={it.id} className="border border-white/10 rounded-sm bg-[#0d0d0d] p-4 flex items-center gap-4">
            {(it.image || it.cover || (it.photos && it.photos[0])) && (
              <img src={it.image || it.cover || it.photos[0]} alt="" className="w-14 h-14 object-cover rounded-sm border border-white/10 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-white font-600 text-sm truncate">{it.name || it.title || it.question || it.city || "Item"}</p>
              <p className="text-zinc-500 text-xs truncate">{it.price || it.category || it.answer || it.description || ""}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(it)} className="p-2 border border-white/15 rounded-sm text-zinc-300 hover:text-gold hover:border-gold" data-testid={`admin-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(it.id)} className="p-2 border border-white/15 rounded-sm text-zinc-300 hover:text-red-400 hover:border-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && <p className="text-zinc-500 text-sm">Nenhum item. Clique em Adicionar.</p>}
      {editing && <ItemModal item={editing} route={route} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function LeadsManager() {
  const { site } = useSite();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/leads").then(({ data }) => setLeads(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm("Excluir lead?")) return; await api.delete(`/admin/leads/${id}`); load(); };

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 text-gold animate-spin mx-auto" /></div>;

  return (
    <div className="overflow-x-auto">
      <p className="text-zinc-400 text-sm mb-4">{leads.length} lead(s) captado(s).</p>
      <table className="w-full text-sm" data-testid="admin-leads-table">
        <thead>
          <tr className="text-left text-zinc-500 border-b border-white/10">
            {["Data", "Nome", "WhatsApp", "Cidade", "Procura", "Origem", "Página", ""].map((h) => <th key={h} className="py-3 pr-4 font-500 whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-b border-white/5 text-zinc-300">
              <td className="py-3 pr-4 whitespace-nowrap text-xs">{(l.created_at || "").slice(0, 10)}</td>
              <td className="py-3 pr-4 whitespace-nowrap text-white">{l.nome}</td>
              <td className="py-3 pr-4 whitespace-nowrap">
                <a href={waLink(site, "barbearia", `Olá ${l.nome}!`)} target="_blank" rel="noreferrer" className="text-gold hover:underline inline-flex items-center gap-1">{l.telefone} <ExternalLink className="w-3 h-3" /></a>
              </td>
              <td className="py-3 pr-4 whitespace-nowrap">{l.cidade}</td>
              <td className="py-3 pr-4 whitespace-nowrap">{l.procura}</td>
              <td className="py-3 pr-4 whitespace-nowrap">{l.origem}</td>
              <td className="py-3 pr-4 whitespace-nowrap text-xs">{l.pagina_origem}</td>
              <td className="py-3"><button onClick={() => remove(l.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {leads.length === 0 && <p className="text-zinc-500 mt-6">Nenhum lead ainda.</p>}
    </div>
  );
}

function SiteEditor() {
  const { refresh } = useSite();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/site").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 text-gold animate-spin mx-auto" /></div>;

  const setNested = (path, value) => {
    setData((d) => {
      const copy = JSON.parse(JSON.stringify(d));
      let cur = copy; const parts = path.split(".");
      for (let i = 0; i < parts.length - 1; i++) { cur[parts[i]] = cur[parts[i]] || {}; cur = cur[parts[i]]; }
      cur[parts[parts.length - 1]] = value; return copy;
    });
  };
  const setProf = (idx, key, value) => setData((d) => { const c = JSON.parse(JSON.stringify(d)); c.professionals[idx][key] = value; return c; });
  const addProf = () => setData((d) => ({ ...d, professionals: [...(d.professionals || []), { id: `p_${Date.now()}`, order: (d.professionals?.length || 0) + 1, name: "", role: "Barbeiro", image: "" }] }));
  const removeProf = (idx) => setData((d) => ({ ...d, professionals: d.professionals.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/site", data); toast.success("Conteúdo atualizado"); refresh(); }
    catch (e) { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const Group = ({ title, children }) => (
    <div className="border border-white/10 rounded-sm bg-[#0d0d0d] p-5">
      <h4 className="overline-label text-xs text-gold mb-4">{title}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
  const F = ({ l, path, textarea }) => {
    const parts = path.split("."); let v = data; parts.forEach((p) => { v = v?.[p]; });
    return (
      <div className={textarea ? "sm:col-span-2" : ""}>
        <label className={label}>{l}</label>
        {textarea ? <textarea rows={5} className={inp} value={v || ""} onChange={(e) => setNested(path, e.target.value)} />
          : <input className={inp} value={v || ""} onChange={(e) => setNested(path, e.target.value)} />}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <Group title="Marca & WhatsApp">
        <F l="Nome da marca" path="brand" />
        <F l="WhatsApp (só números, com DDI)" path="whatsapp" />
        <F l="Link de assinaturas (Frizzar)" path="frizzar_url" />
        <F l="Logo Prime Capilar (URL)" path="logo_prime" textarea />
        <F l="Logo Barbearia (URL)" path="logo_barbearia" textarea />
      </Group>
      <Group title="Hero (Home)">
        <F l="Título linha 1" path="hero.title_line1" />
        <F l="Título linha 2" path="hero.title_line2" />
        <F l="Subtítulo" path="hero.subtitle" textarea />
        <F l="Imagem do hero (URL)" path="hero.image" textarea />
      </Group>
      <Group title="Quem Somos / Bruno Barber">
        <F l="Nome" path="about.owner_name" />
        <F l="Imagem (URL)" path="about.owner_image" />
        <F l="Texto institucional" path="about.text" textarea />
        <F l="Foto da equipe / Barbearia (URL)" path="team_photo" textarea />
      </Group>
      <Group title="Mensagens automáticas do WhatsApp">
        <F l="Barbearia" path="whatsapp_messages.barbearia" textarea />
        <F l="Arapiraca (prótese)" path="whatsapp_messages.arapiraca" textarea />
        <F l="Garanhuns" path="whatsapp_messages.garanhuns" textarea />
        <F l="Maceió" path="whatsapp_messages.maceio" textarea />
      </Group>
      <Group title="Redes sociais">
        <F l="Instagram Barbearia" path="socials.instagram_barbearia" />
        <F l="Instagram Prime Capilar" path="socials.instagram_prime" />
        <F l="Instagram Bruno Barber" path="socials.instagram_bruno" />
        <F l="Instagram Arena Barbearia" path="socials.instagram_arena" />
        <F l="Google / Maps" path="socials.google" />
      </Group>

      <div className="border border-white/10 rounded-sm bg-[#0d0d0d] p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="overline-label text-xs text-gold">Profissionais (equipe)</h4>
          <button onClick={addProf} className="inline-flex items-center gap-1.5 text-gold text-sm"><Plus className="w-4 h-4" /> Adicionar</button>
        </div>
        <div className="space-y-3">
          {(data.professionals || []).map((p, i) => (
            <div key={p.id || i} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end border-b border-white/5 pb-3">
              <div><label className={label}>Nome</label><input className={inp} value={p.name || ""} onChange={(e) => setProf(i, "name", e.target.value)} /></div>
              <div><label className={label}>Função</label><input className={inp} value={p.role || ""} onChange={(e) => setProf(i, "role", e.target.value)} /></div>
              <div className="flex gap-2">
                <div className="flex-1"><label className={label}>Foto (URL)</label><input className={inp} value={p.image || ""} onChange={(e) => setProf(i, "image", e.target.value)} /></div>
                <button onClick={() => removeProf(i)} className="p-2.5 border border-white/15 rounded-sm text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end sticky bottom-4">
        <button onClick={save} disabled={saving} className="px-8 py-3 bg-gold hover:bg-gold-hover text-black font-heading font-600 rounded-sm inline-flex items-center gap-2 shadow-xl" data-testid="admin-save-site">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} SALVAR ALTERAÇÕES
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    if (!loading && !user) navigate("/admin");
  }, [user, loading, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  if (!user) return null;
  if (!user.is_admin) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      <p className="text-white font-heading text-xl">Acesso restrito ao administrador</p>
      <p className="text-zinc-500 text-sm">A conta {user.email} não tem permissão para este painel.</p>
      <button onClick={() => { logout(); navigate("/admin"); }} className="px-5 py-2.5 border border-white/20 rounded-sm text-zinc-300 text-sm">Sair</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-gold fill-gold" />
            <span className="font-heading font-600 text-white">PAINEL ADMINISTRATIVO</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-zinc-400 hover:text-white hidden sm:inline">Ver site</a>
            <button onClick={() => { logout(); navigate("/admin"); }} className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-gold" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Sair</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`font-heading font-500 text-sm px-4 py-2 rounded-sm border transition-colors ${tab === t.key ? "bg-gold text-black border-gold" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsManager />}
        {tab === "site" && <SiteEditor />}
        {["plans", "services", "results", "testimonials", "units", "faqs", "posts"].includes(tab) && <EntityManager route={tab} key={tab} />}
      </div>
    </div>
  );
}
